import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

interface FormData {
  title: string;
  introduction: string;
  phoneNumber: string;
  highestDegree: string;
  graduatedUniversity: string;
  graduationYear: string;
  major: string;
  teachingExperience: string;
  applicationNotes: string;
}

const TeacherApplication = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    introduction: '',
    phoneNumber: '',
    highestDegree: '',
    graduatedUniversity: '',
    graduationYear: '',
    major: '',
    teachingExperience: '',
    applicationNotes: ''
  });

  useEffect(() => {
    loadExistingApplication();
  }, []);

  const loadExistingApplication = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/teachers/application-status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.id) {
          setApplicationId(data.id);
          // 加载现有申请数据到表单
          setFormData({
            title: data.title || '',
            introduction: data.introduction || '',
            phoneNumber: data.phoneNumber || '',
            highestDegree: data.highestDegree || '',
            graduatedUniversity: data.graduatedUniversity || '',
            graduationYear: data.graduationYear || '',
            major: data.major || '',
            teachingExperience: data.teachingExperience || '',
            applicationNotes: data.applicationNotes || ''
          });
        }
      }
    } catch (error) {
      console.error('加载申请数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const url = applicationId 
        ? `/api/teachers/applications/${applicationId}` 
        : '/api/teachers/applications';
      
      const method = applicationId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('申请提交成功！');
        navigate('/teacher/application-status');
      } else {
        const error = await response.json();
        alert(error.message || '提交失败，请重试');
      }
    } catch (error) {
      console.error('提交失败:', error);
      alert('网络错误，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 头部 */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-900">
            {applicationId ? '修改教师申请' : '教师申请'}
          </h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/teacher/application-status')}
              className="text-gray-600 hover:text-gray-800"
            >
              返回状态页面
            </button>
            <button
              onClick={handleLogout}
              className="text-gray-600 hover:text-gray-800"
            >
              退出登录
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              {applicationId ? '修改您的申请信息' : '填写申请信息'}
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              请仔细填写以下信息，确保内容准确完整
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* 基本信息 */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">基本信息</h3>
              
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  职业头衔 *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="例如：数学高级教师、英语专业讲师"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  手机号码 *
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  required
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="请输入您的手机号码"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="introduction" className="block text-sm font-medium text-gray-700 mb-1">
                  个人简介 *
                </label>
                <textarea
                  id="introduction"
                  name="introduction"
                  required
                  rows={4}
                  value={formData.introduction}
                  onChange={handleChange}
                  placeholder="请简要介绍您的教学背景、专业特长等"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 学历信息 */}
            <div className="space-y-4 border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900">学历背景</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="highestDegree" className="block text-sm font-medium text-gray-700 mb-1">
                    最高学历 *
                  </label>
                  <select
                    id="highestDegree"
                    name="highestDegree"
                    required
                    value={formData.highestDegree}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">请选择</option>
                    <option value="本科">本科</option>
                    <option value="硕士">硕士</option>
                    <option value="博士">博士</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="graduationYear" className="block text-sm font-medium text-gray-700 mb-1">
                    毕业年份 *
                  </label>
                  <input
                    type="number"
                    id="graduationYear"
                    name="graduationYear"
                    required
                    min="1980"
                    max={new Date().getFullYear()}
                    value={formData.graduationYear}
                    onChange={handleChange}
                    placeholder="例如：2020"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="graduatedUniversity" className="block text-sm font-medium text-gray-700 mb-1">
                  毕业院校 *
                </label>
                <input
                  type="text"
                  id="graduatedUniversity"
                  name="graduatedUniversity"
                  required
                  value={formData.graduatedUniversity}
                  onChange={handleChange}
                  placeholder="请输入毕业院校名称"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="major" className="block text-sm font-medium text-gray-700 mb-1">
                  所学专业 *
                </label>
                <input
                  type="text"
                  id="major"
                  name="major"
                  required
                  value={formData.major}
                  onChange={handleChange}
                  placeholder="请输入专业名称"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 教学经验 */}
            <div className="space-y-4 border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900">教学经验</h3>
              
              <div>
                <label htmlFor="teachingExperience" className="block text-sm font-medium text-gray-700 mb-1">
                  教学经验描述 *
                </label>
                <textarea
                  id="teachingExperience"
                  name="teachingExperience"
                  required
                  rows={6}
                  value={formData.teachingExperience}
                  onChange={handleChange}
                  placeholder="请详细描述您的教学经验、教学方法、所教授课程等"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 申请说明 */}
            <div className="space-y-4 border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900">申请说明</h3>
              
              <div>
                <label htmlFor="applicationNotes" className="block text-sm font-medium text-gray-700 mb-1">
                  申请说明（可选）
                </label>
                <textarea
                  id="applicationNotes"
                  name="applicationNotes"
                  rows={3}
                  value={formData.applicationNotes}
                  onChange={handleChange}
                  placeholder="如有其他需要说明的情况，请在此填写"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 提交按钮 */}
            <div className="border-t pt-6">
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/teacher/application-status')}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? '提交中...' : (applicationId ? '更新申请' : '提交申请')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TeacherApplication;
